import type { AppNotification } from "../types/notifications";
import type { ExportDataSource, ExportEntityType } from "./exportApiHandlers";
import { buildExportHistoryDeepLink } from "./exportHistoryDeepLink";

export const LOCAL_EXPORT_NOTIFICATION_PREFIX = "local-export-";

export function localExportNotificationId(exportId: string): string {
  return `${LOCAL_EXPORT_NOTIFICATION_PREFIX}${exportId}`;
}

export function isLocalExportNotificationId(id: string): boolean {
  return id.startsWith(LOCAL_EXPORT_NOTIFICATION_PREFIX);
}

export function isExportCompletionNotification(notification: {
  eventType?: string;
  _id?: string;
  templateKey?: string;
  entityType?: string;
}): boolean {
  return (
    notification.eventType === "ExportCompleted" ||
    notification.eventType === "ExportFailed" ||
    notification.templateKey === "EXPORT_COMPLETED" ||
    notification.templateKey === "EXPORT_FAILED" ||
    notification.entityType === "EXPORT" ||
    Boolean(notification._id && isLocalExportNotificationId(notification._id))
  );
}

function entityTypeForNotification(
  entity: ExportEntityType
): AppNotification["entityType"] {
  switch (entity) {
    case "patients":
      return "PATIENT";
    case "tasks":
      return "TASK";
    default:
      return "OPPORTUNITY";
  }
}

export function buildExportCompletionNotification(input: {
  exportId: string;
  entityType: ExportEntityType;
  entityLabel: string;
  source?: ExportDataSource;
  failed?: boolean;
  recipientUserId?: string;
  tenantId?: string;
}): AppNotification {
  const now = new Date().toISOString();
  const failed = Boolean(input.failed);
  const label = input.entityLabel || "data";
  return {
    _id: localExportNotificationId(input.exportId),
    tenantId: input.tenantId || "",
    recipientUserId: input.recipientUserId || "",
    templateKey: "SYSTEM_EVENT",
    eventType: failed ? "ExportFailed" : "ExportCompleted",
    priority: failed ? "HIGH" : "HIGH",
    priorityOrder: 3,
    entityType: entityTypeForNotification(input.entityType),
    entityId: input.exportId,
    actorType: "SYSTEM",
    title: failed
      ? `Your ${label} export failed`
      : `Your ${label} export is ready`,
    body: failed
      ? "Open export history to retry or see details."
      : "Open export history to download or check delivery.",
    iconKey: failed ? "alert-triangle" : "info",
    deepLinkPath: buildExportHistoryDeepLink({
      entityType: input.entityType,
      source: input.source ?? "overview",
      exportId: input.exportId,
    }),
    aggregationCount: 1,
    surfaces: {
      drawer: true,
      toast: true,
      toastDurationMs: 5000,
      listRowHighlight: false,
    },
    channels: { inApp: true },
    expiresAt: now,
    createdAt: now,
    updatedAt: now,
  };
}
